import { z } from 'zod'

export const salonStages = ['registered', 'salon', 'services', 'customers', 'bookings', 'paying'] as const
export const salonStageSchema = z.enum(salonStages)

export const loginMethods = ['email', 'google', 'apple', 'facebook'] as const
export const loginMethodSchema = z.enum(loginMethods)

const isoDate = z.iso.datetime().nullable()

// Публикация в каталоге Manaro. Отклонённый салон ждёт исправлений от владельца, а не проверки команды.
export const moderationStates = ['pending', 'rejected', 'published', 'blocked'] as const
export const moderationStateSchema = z.enum(moderationStates)

export const moderationActions = ['approved', 'rejected', 'unpublished', 'resubmitted'] as const
export const moderationActionSchema = z.enum(moderationActions)

export const salonModerationSchema = z.object({
  state: moderationStateSchema,
  comment: z.string().nullable(),
  moderatedAt: isoDate,
  lastEvent: z
    .object({
      action: moderationActionSchema,
      actor: z.string(),
      reason: z.string().nullable(),
      at: isoDate,
    })
    .nullable(),
})

export const MODERATION_REASON_MAX = 500

const moderationReason = z
  .string()
  .trim()
  .min(1, 'Напишите владельцу, что исправить.')
  .max(MODERATION_REASON_MAX, `Не длиннее ${MODERATION_REASON_MAX} символов.`)

export const salonModerationRequestSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approve') }),
  z.object({ decision: z.literal('reject'), reason: moderationReason }),
  z.object({ decision: z.literal('unpublish'), reason: moderationReason }),
])

export const salonModerationResultSchema = z.object({
  salonId: z.number(),
  status: z.string(),
  moderation: salonModerationSchema,
  ownerNotified: z.boolean(),
})

/** Paid plans that can be given free until a date (sales deals, «3 месяца бесплатно»). */
export const giftablePlans = ['solo', 'start', 'pro', 'network'] as const
export const PLAN_GRANT_NOTE_MAX = 500
/** The backend refuses a gift longer than this. */
export const PLAN_GRANT_MAX_MONTHS = 12

export const salonPlanGrantRequestSchema = z.object({
  plan: z.enum(giftablePlans, { error: 'Выберите тариф.' }),
  until: z.iso.date({ error: 'Укажите, до какого числа.' }),
  note: z
    .string()
    .trim()
    .max(PLAN_GRANT_NOTE_MAX, `Не длиннее ${PLAN_GRANT_NOTE_MAX} символов.`)
    .optional(),
})

export const salonPlanGrantResultSchema = z.object({
  salonId: z.number(),
  plan: z.string(),
  planRenewsAt: isoDate,
  grantedBy: z.string(),
})

export const signupSourceSchema = z.object({
  client: z.string(),
  channel: z.string(),
  source: z.string().nullable(),
  medium: z.string().nullable(),
  campaign: z.string().nullable(),
  landingPath: z.string().nullable(),
})

export const salonOwnerSchema = z.object({
  id: z.number(),
  email: z.string(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  registeredAt: isoDate,
  lastActiveAt: isoDate,
  login: loginMethodSchema,
  crmAccess: z.boolean(),
  looksLikeTest: z.boolean(),
  signup: signupSourceSchema.nullable(),
})

/** Как связаться с салоном — так, как владелец заполнил профиль. */
export const salonContactsSchema = z.object({
  phones: z.array(z.string()),
  email: z.string().nullable(),
  telegram: z.string().nullable(),
  viber: z.string().nullable(),
  whatsapp: z.string().nullable(),
  instagram: z.string().nullable(),
  facebook: z.string().nullable().default(null),
  website: z.string().nullable(),
  /** Способы, которые салон отметил как удобные: phone, email, telegram, viber, whatsapp, instagram, other. */
  preferred: z.array(z.string()),
})

export const salonSummarySchema = z.object({
  id: z.number(),
  title: z.string(),
  status: z.string(),
  /** null — бэкенд Manaro ещё без модерации: действия скрыты. */
  moderation: salonModerationSchema.nullable(),
  createdAt: isoDate,
  city: z.string().nullable(),
  address: z.string().nullable(),
  /** Язык писем владельцу: на нём стоит писать причину отказа. */
  language: z.string().nullable(),
  image: z.string().nullable(),
  slug: z.string().nullable(),
  publicUrl: z.string().nullable(),
  phone: z.string().nullable(),
  instagram: z.string().nullable(),
  /** null — бэкенд Manaro ещё без контактов: остаются phone и instagram. */
  contacts: salonContactsSchema.nullable(),
  currency: z.string().nullable(),
  plan: z.string().nullable(),
  planRenewsAt: isoDate,
  /** The plan was given free until planRenewsAt; false on backends without gifts. */
  planGift: z.boolean().default(false),
  pendingPlan: z.string().nullable(),
  services: z.number(),
  masters: z.number(),
  customers: z.number(),
  orders: z.number(),
  orders30d: z.number(),
  completedOrders: z.number(),
  lastOrderAt: isoDate,
  payments: z.number(),
  lastPaymentAt: isoDate,
})

export const salonRowSchema = z.object({
  user: salonOwnerSchema,
  salon: salonSummarySchema.nullable(),
  stage: salonStageSchema,
})

export const salonsResponseSchema = z.object({
  available: z.boolean(),
  generatedAt: isoDate,
  catalogHidden: z.number(),
  rows: z.array(salonRowSchema),
})

export type SalonStage = z.infer<typeof salonStageSchema>
export type LoginMethod = z.infer<typeof loginMethodSchema>
export type SignupSource = z.infer<typeof signupSourceSchema>
export type SalonRow = z.infer<typeof salonRowSchema>
export type SalonsResponse = z.infer<typeof salonsResponseSchema>
export type SalonContacts = z.infer<typeof salonContactsSchema>
export type ModerationState = z.infer<typeof moderationStateSchema>
export type ModerationAction = z.infer<typeof moderationActionSchema>
export type SalonModeration = z.infer<typeof salonModerationSchema>
export type SalonModerationRequest = z.infer<typeof salonModerationRequestSchema>
export type SalonModerationResult = z.infer<typeof salonModerationResultSchema>
export type GiftablePlan = (typeof giftablePlans)[number]
export type SalonPlanGrantRequest = z.infer<typeof salonPlanGrantRequestSchema>
export type SalonPlanGrantResult = z.infer<typeof salonPlanGrantResultSchema>
